import {IBizPromotion, IBusiness} from "@/views/editor/service/business";
import {AEditor} from "@/views/editor/service/editor";
import {EActivityStatus, IActivityBusinessMeta} from "@/model/activity";
import {APromotion} from "@/views/editor/service/promotion/promotion";
import {CheckboxOptionType} from "ant-design-vue/es/checkbox/interface";
import {Component, watch} from "vue";
import FragmentFullGroups from "@/views/editor/service/promotion/tier_group/FragmentFullGroups.vue";
import {ICouponPolicy, IInput, INotice, IProduct} from "@/views/editor/service/types";
import {WidgetSkuPdd} from "@/views/editor/widget/sku/pdd";
import {INewWidget, IWidget} from "@/views/editor/widget";

export interface ITierGroupProduct extends IProduct {
    regular_price: number;
    virtual_stock: number;
    images: string[];
    gifts: string[];
    tiers: ITier[];
    gifts_limit: number;
    is_detail_mode: boolean;
    pay_button_text: string;
}

export interface ITier {
    size: number;
    price: number;
    gifts: string[];
}

export interface ITierGroupMeta {
    products: ITierGroupProduct[];
    props: {
        is_show_group_list: boolean;
        is_use_group_share_thumb_image: boolean;
        goods_validity_mons: number;
        customer_phone: {
            available: boolean;
            // 报名时区分新老客
            sign: boolean,
            leader_permission: 'all' | 'old' | 'new';
            joiner_permission: 'all' | 'old' | 'new';
        };
        buy_limit: number;
        joiner_to_leader: {
            available: boolean;
            is_fill_virtual_joiner: boolean;
        };
        is_coupon_leader_on: boolean;
        is_coupon_leader_free_on: boolean;
        widget: {
            info: string
            leader: string
            sku: string
        };
    }
}

export default class TierGroup extends APromotion implements IBizPromotion {

    readonly title = '阶梯团'
    readonly opt: ITierGroupMeta
    readonly widgets: Record<string, Record<string, IWidget<unknown>>> = {}

    constructor(protected readonly editor: AEditor, readonly meta: IActivityBusinessMeta) {
        super(editor, meta);
        this.opt = this.meta.setting as ITierGroupMeta
        this.widgets['sku'] = {'widget.sku.pdd': new WidgetSkuPdd(editor, {})}
    }

    subs(status: EActivityStatus): CheckboxOptionType[] {
        return {
            active: [],
            preheating: [],
            finished: []
        }[status]
    }

    resetMeta(): IBusiness {
        // if (this.editor.userAgent.authorizer().grant(EVipLevel.Professional))
        // this.editor.biz.active.coupon?.set('tier_group', [])

        const opt: ITierGroupMeta = {
            products: [],
            props: {
                is_show_group_list: true,
                is_use_group_share_thumb_image: false,
                goods_validity_mons: 3,
                customer_phone: {
                    available: false,
                    sign: true,
                    leader_permission: 'all',
                    joiner_permission: 'all'
                },
                buy_limit: 1,
                joiner_to_leader: {
                    available: false,
                    is_fill_virtual_joiner: false
                },
                is_coupon_leader_free_on: true,
                is_coupon_leader_on: true,
                widget: {
                    info: 'widget.info.pdd',
                    leader: 'widget.leader.pdd',
                    sku: 'widget.sku.pdd'
                }
            }
        }
        Object.assign(this.opt, opt)
        this.opt.products = [this.emptyProduct()]
        return this;
    }

    price(): number {
        return this.opt.products.reduce((sum, product) => sum = sum > product.price ? sum : product.price, 0);
    }

    emptyProduct(): ITierGroupProduct {
        return {
            gifts: [],
            gifts_limit: 0,
            images: [],
            is_detail_mode: false,
            name: "在这里填写商品描述",
            origin_price: 200,
            pay_button_text: "立即开团",
            price: 100,
            regular_price: 0,
            stock: 500,
            thumbnail: 'https://static.pms.guandb.cn/media/system/console/20260302195412.jpg',
            tiers: [{size: 0, price: 100, gifts: []}, {size: 2, price: 100, gifts: []}],
            virtual_stock: 0,
            sku: {
                enabled: true,
                title: '选择门店',
                sub_title: '',
                check_type: "radio",
                limit: {
                    min: 0,
                    max: 0
                },
                is_group: true,
                group: {},
                options: [],
                widget_meta: this.widgets['sku'][this.opt.props.widget.sku].defaultMeta(),
            },
        }
    }

    lookersListView(): { title: string, component: Component } {
        return {title: '满团记录', component: FragmentFullGroups}
    }

    apply(): IBusiness {
        const i: IInput = {
            input_type: 'text', max: 0, min: 0,
            items: ['新客', '老客'],
            deletable: false,
            editable: true,
            id: "group.customer.phones",
            origin: this.title,
            required: true,
            title: "请选择身份",
            type: 'role'
        }
        watch(() => this.opt.props.customer_phone.sign, o => {
            this.editor.biz.active.sign?.set(this.title, o ? [i] : [])
        })
        this.editor.biz.active.sign?.set(this.title, this.opt.props.customer_phone.sign ? [i] : [])

        const notices: INotice[] = [
            {origin: this.title, id: 'buy_limit', notice: `每人限购${this.opt.props.buy_limit}单`},
            {
                origin: this.title,
                id: 'goods_validity_mons',
                notice: `购买后${this.opt.props.goods_validity_mons}个月内到店有效`
            },
        ]
        this.editor.biz.active.atmosphere?.setRules(this.title, notices)
        const policies: ICouponPolicy[] = [
            {
                id: 'group_leader_coupon',
                origin: this.title,
                name: '团长优惠券',
                type: 'cash',
                value: 1,
                send_limit: 100,
                validity_duration_minutes: 60
            },
            {
                id: 'group_leader_free_coupon',
                origin: this.title,
                name: '团长免单券',
                type: 'free',
                value: 0,
                send_limit: 100,
                validity_duration_minutes: 6
            }
        ]
        this.editor.biz.active.coupon?.setPolicies(this.title, policies)
        this.editor.biz.active.sign?.setButtonText('立即开团')
        return this
    }

    products(): IProduct[] {
        return this.opt.products
    }

    destructor() {
        super.destructor();
        this.editor.biz.active.atmosphere?.setRules(this.title, [])
        this.editor.biz.active.coupon?.setPolicies(this.title, [])
        this.editor.biz.active.sign?.set(this.title, [])
    }

    skuDisplay(): Component {
        return this.widgets['sku'][this.opt.props.widget.sku].display()
    }
}